import { DeliveryTrackingForOrder } from 'redux/modules/delivery-tracking/reducer/initial-state';
import { dayjs, FORMAT, format, isSameDay, isTomorrow } from 'utils/date';
import { DeliveryStatus } from 'constants/deliveryStatus';
import { dataLayer } from './data-layer';

const wismoStatusForAnalytics: Record<DeliveryStatus, string> = {
  CANCELLED: 'Cancelled',
  DELIVERED: 'Delivered',
  MISSED: 'We Missed You',
  NEXT: 'Next',
  OUT_FOR_DELIVERY: 'Out for Delivery',
  PENDING: 'Pending',
  PICKED: 'Picked',
  PLACED: 'Placed',
  REFUND_PENDING: 'Refund Pending',
  UNKNOWN: 'Unknown',
};

function getDeliveryDay(date: dayjs.ConfigType) {
  const tzDate = dayjs(date).tz();
  if (isSameDay(tzDate, dayjs().tz())) {
    return 'Today';
  }

  if (isTomorrow(tzDate)) {
    return 'Tomorrow';
  }

  return undefined;
}

function getDeliveryWindowStr(
  wismoDeliveryDateStart: string | undefined,
  wismoDeliveryDateEnd: string | undefined,
) {
  if (!wismoDeliveryDateStart || !wismoDeliveryDateEnd) {
    return '30 minutes';
  }

  const deliveryWindowDiff = dayjs.duration(
    dayjs(wismoDeliveryDateEnd).tz().diff(dayjs(wismoDeliveryDateStart).tz()),
  );

  const hours = deliveryWindowDiff.get('hours');
  if (hours >= 2) {
    return `${hours} hours`;
  }
  if (hours >= 1) {
    return `1 hour`;
  }

  const minutes = deliveryWindowDiff.get('minutes');
  if (minutes < 30) {
    return '30 minutes';
  }

  return `${minutes} minutes`;
}

function getWismoDataLayerProps(deliveryInfo: DeliveryTrackingForOrder) {
  const wismoDeliveryDateStart = format(
    dayjs(deliveryInfo.wismoEtaStartDateTime).tz(),
    FORMAT.ISO8601_DATE_TIME,
  );
  const wismoDeliveryDateEnd = format(
    dayjs(deliveryInfo.wismoEtaEndDateTime).tz(),
    FORMAT.ISO8601_DATE_TIME,
  );
  const windowDiffStr = getDeliveryWindowStr(
    deliveryInfo.wismoEtaStartDateTime,
    deliveryInfo.wismoEtaEndDateTime,
  );

  const deliveryDay = getDeliveryDay(deliveryInfo.wismoEtaEndDateTime);

  return {
    // Order Information
    wismo_order_number: deliveryInfo.customerOrderId,
    wismo_status: wismoStatusForAnalytics[deliveryInfo.status] ?? wismoStatusForAnalytics.UNKNOWN,

    // Delivery Timing Details
    // Date/Time format: YYYY-MM-DDTHH:MM:SS (ISO 8601 Standard) in Europe/London time-zone
    wismo_delivery_date_start: wismoDeliveryDateStart,
    wismo_delivery_date_end: wismoDeliveryDateEnd,

    // Delivery Window (Calculated based on start/end time difference)
    wismo_delivery_window: windowDiffStr,

    // Delivery Day (Calculated based on current date)
    ...(deliveryDay ? { wismo_delivery_day: deliveryDay } : null),

    // Warning Message
    ...(deliveryInfo.runningLate ? { wismo_warning: 'we are running late' } : undefined),
  };
}

export function trackWismoImpression(deliveryInfo: DeliveryTrackingForOrder) {
  dataLayer.push({
    ...getWismoDataLayerProps(deliveryInfo),
    // Standard GTM/GA4 naming convention for the event trigger
    event: 'wismo_impression',
  });
}

export function trackWismoClickViewOrder(deliveryInfo: DeliveryTrackingForOrder) {
  dataLayer.push({
    ...getWismoDataLayerProps(deliveryInfo),
    // Standard GTM/GA4 naming convention for the event trigger
    event: 'wismo_click_view_order',
  });
}
